import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Joi from 'joi';
import isEqual from 'lodash/isEqual';
import clsx from 'clsx';

import { tunnelSelectors, userSelectors } from 'reducers';
import { initDistributorPayload } from 'schemas/tunnel';

import { useGetRolesQuery, useSignUpMutation } from 'api/main';
import { getQueryParameterString, validateSchema } from 'utils/helpers';
import { useTunnelChangeHandlers } from 'utils/hooks';
import { corporateForms, titles, positions } from 'utils/formData';
import {
    PARANTHESES_EURO_SYMBOL,
    InternalErrors,
    investorErrors,
    PASS_REGEX,
    stepStatus,
    EMAIL_VALIDATION_OPTIONS,
    userAvailabilityStatus,
    NAME_REGEX,
    COMPANY_NAME_REGEX,
} from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { TunnelBox } from 'containers/tunnel/common';
import PasswordForm from './PasswordForm';
import ConditionsForm from 'containers/tunnel/steps/contact-info/forms/ConditionsForm';
import {
    CustomSelect,
    ErrorMessage,
    CustomTextField,
    SubmitButton,
    CustomPhoneField,
    CustomFormLabel,
    FullAddressForm,
    RadioLabel,
} from 'components';
import { MenuItem, Typography, Box, FormControl, RadioGroup } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    field: {
        width: '100%',
        maxWidth: theme.constants.tunnelInputWidth,
    },
    subtitle: {
        marginBottom: theme.spacing(2),
    },
    separator: {
        marginTop: theme.spacing(3),
        display: 'block',
        marginLeft: 'auto',
    },
    labelColor: {
        color: theme.palette.text.secondary,
    },
}));

const renderOption = opt => (
    <MenuItem key={opt} value={opt}>
        {opt}
    </MenuItem>
);

const DistributorForm = props => {
    const { userStep, roleName, tunnelStep, handleNextStep } = props;
    const classes = useStyles();

    const setTunnelChangesHandlers = useTunnelChangeHandlers();
    const { data: roles = [] } = useGetRolesQuery();
    const [signUp, { isLoading, isSuccess, isError, error, reset }] = useSignUpMutation({
        fixedCacheKey: 'register',
    });

    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelErrors = useSelector(tunnelSelectors.getTunnelErrors);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const defaultForm = useMemo(
        () => initDistributorPayload(internalUser, tunnelSubscription),
        [tunnelSubscription, internalUser]
    );
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        otherCorporateForm: '',
        organization: '',
    });
    const label = getQueryParameterString(useLocation().search, 'label');

    useEffect(() => {
        setForm(defaultForm);
    }, [defaultForm]);

    const [emailExists, setEmailExists] = useState(false);
    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const isAccountNotConfirmed = useMemo(
        () => internalUser?.availabilityStatus === userAvailabilityStatus.PENDING_PASSWORD_CHANGE,
        [internalUser]
    );
    const roleId = useMemo(() => roles.find(r => r.name === roleName)?.id, [roles, roleName]);
    const userStepOk = useMemo(
        () => [stepStatus.COMPLETED, stepStatus.VERIFIED].includes(userStep?.status),
        [userStep]
    );
    const userStepFailed = useMemo(() => userStep?.status === stepStatus.FAILED, [userStep]);

    const schema = useMemo(
        () => ({
            lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
                'string.min': texts.LAST_NAME_LENGTH,
                'string.max': texts.LAST_NAME_LENGTH,
                'string.pattern.base': texts.LAST_NAME_PATTERN,
            }),
            firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PRENOM,
                'string.min': texts.FIRST_NAME_LENGTH,
                'string.max': texts.FIRST_NAME_LENGTH,
                'string.pattern.base': texts.FIRST_NAME_PATTERN,
            }),
            password: Joi.string().pattern(PASS_REGEX).required().messages({
                'string.empty': texts.EMPTY_NEW_PASSWORD,
                'string.pattern.base': texts.LE_MOT_DE_PASSE_DOIT_CONTENIR,
            }),
            confirmPassword: Joi.when('password', {
                is: '',
                then: Joi.any(),
                otherwise: Joi.ref('password'),
            }).messages({ 'any.only': texts.PASSWORDS_MUST_MATCH }),
            currentAddress: {
                address: Joi.string()
                    .required()
                    .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_ADDRESS }),
                zipCode: Joi.string()
                    .required()
                    .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_LE_CODE_POSTAL }),
                city: Joi.string()
                    .required()
                    .messages({ 'string.empty': texts.VEUILLEZ_SAISIR_LA_VILLE }),
            },
            shareCapital: Joi.number()
                .precision(3)
                .required()
                .messages({ 'number.base': texts.SAISIR_LE_CHAMP }),
            corporateName: Joi.string().required().pattern(COMPANY_NAME_REGEX).messages({
                'string.empty': texts.FIELD_REQUIRED,
                'string.pattern.base': texts.INVALID_INPUT,
            }),
            corporateForm: Joi.string()
                .required()
                .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            otherCorporateForm: Joi.when('corporateForm', {
                is: Joi.string().equal(corporateForms.other),
                then: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
                otherwise: Joi.any(),
            }),
            organization: Joi.string().required().messages({
                'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
            }),
            email: Joi.string().email(EMAIL_VALIDATION_OPTIONS).required().messages({
                'string.empty': texts.SAISIR_LE_CHAMP,
                'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
            }),
            phoneNumber: Joi.string().required().messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_TELEPHONE,
                'any.invalid': texts.VEUILLEZ_SAISIR_VOTRE_TELEPHONE,
            }),
            legalRepresentant: Joi.string().required().messages({
                'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
            }),
            representativeName: Joi.when('legalRepresentant', {
                is: Joi.string().equal('no'),
                then: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
                otherwise: Joi.any(),
            }),
            conditions: Joi.object({
                termsAgreement: Joi.boolean().valid(true),
                acceptedEmails: Joi.boolean().valid(true),
                subscribedToNewsletter: Joi.any(),
            }),
        }),
        []
    );

    const resubmitSchema = useMemo(
        () => ({
            firstName: schema.firstName,
            lastName: schema.lastName,
            corporateName: schema.corporateName,
            corporateForm: schema.corporateForm,
            otherCorporateForm: schema.otherCorporateForm,
            organization: schema.organization,
            shareCapital: schema.shareCapital,
            phoneNumber: schema.phoneNumber,
            currentAddress: schema.currentAddress,
            legalRepresentant: schema.legalRepresentant,
            representativeName: schema.representativeName,
            conditions: schema.conditions,
        }),
        [schema]
    );

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(isResubmit ? resubmitSchema : schema, form);

        setErrors({
            ...newErrors,
            address: newErrors['currentAddress,address'],
            city: newErrors['currentAddress,city'],
            zipCode: newErrors['currentAddress,zipCode'],
        });

        return valid;
    }, [form, isResubmit, schema, resubmitSchema]);

    const allowSubmit = useMemo(
        () =>
            form.phoneNumber !== '' && // phoneNumber is empty
            form.legalRepresentant &&
            form.shareCapital &&
            form.corporateName &&
            form.corporateForm &&
            form.organization &&
            form.currentAddress.country &&
            form.currentAddress.address &&
            form.currentAddress.zipCode &&
            form.currentAddress.city &&
            form.conditions.termsAgreement &&
            form.conditions.acceptedEmails &&
            (form.corporateForm === corporateForms.other ? form.otherCorporateForm : true) &&
            (internalUser ? true : form.password && form.confirmPassword) &&
            (form.legalRepresentant === 'no' ? form.representativeName : true),
        [form, internalUser]
    );

    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setForm(oldValue => ({ ...oldValue, [key]: value }));

            if (key === 'corporateForm' && value !== corporateForms.other) {
                setForm(oldValue => ({ ...oldValue, otherCorporateForm: '' }));
            }

            setEmailExists(false); // remove the error message on any field change
        },
        []
    );

    const handlePhoneChange = useCallback(phone => {
        setForm(oldValue => ({ ...oldValue, phoneNumber: phone }));
    }, []);

    // returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => {
        return !isEqual(defaultForm, form);
    }, [form, defaultForm]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    const handleAddressChange = useCallback(address => {
        setForm(prevState => ({
            ...prevState,
            currentAddress: { ...prevState.currentAddress, ...address },
        }));
    }, []);

    const handleConditionsChange = useCallback(e => {
        const { name, checked } = e.target;
        setForm(prevValue => ({
            ...prevValue,
            conditions: { ...prevValue.conditions, [name]: checked },
        }));
    }, []);

    const handleSubmit = useCallback(
        () => () => {
            if (
                ((userStepOk && userStep?.tunnelStep.resubmitIfOK) ||
                    (userStepFailed && userStep?.tunnelStep.resubmitIfFailed)) &&
                formIsValid()
            ) {
                const { password, confirmPassword, ...rest } = form;
                signUp({
                    resubmit: true,
                    data: {
                        ...rest,
                        fundId: '0',
                        fundName: '',
                        roleId: roleId + '',
                        stepId: tunnelStep.step.id,
                        roleName,
                        label,
                    },
                });
            } else if (formIsValid()) {
                signUp({
                    resubmit: false,
                    data: {
                        ...form,
                        fundId: '0',
                        fundName: '',
                        roleId: roleId + '',
                        stepId: tunnelStep.step.id,
                        roleName,
                        label,
                    },
                });
            }
        },
        [
            form,
            roleId,
            signUp,
            roleName,
            userStep,
            formIsValid,
            userStepOk,
            userStepFailed,
            tunnelStep,
            label,
        ]
    );

    useEffect(() => {
        if (isSuccess && (isResubmit || userStep)) {
            reset();
            handleNextStep(true);
        }
    }, [isSuccess, isResubmit, handleNextStep, userStep, reset]);

    useEffect(() => {
        if (isError) {
            if (error?.status === 409 && error?.data?.message === InternalErrors.USERNAME_EXISTS) {
                setEmailExists(true);
            } else if (error?.message === investorErrors.INVALID_PHONE) {
                setErrors(prevState => ({
                    ...prevState,
                    phoneNumber: texts.INVALID_PHONE,
                }));
            }
        }
    }, [isError, error]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, !!internalUser),
        [internalUser, checkChanges, handleSubmit, setTunnelChangesHandlers]
    );

    return (
        <React.Fragment>
            {!isSuccess && (
                <TunnelBox>
                    {!!Object.keys(tunnelErrors).length && (
                        <ErrorMessage gutterBottom message={texts.FORM_HAS_ERRORS} />
                    )}
                    <Typography variant="h6" gutterBottom className={classes.subtitle}>
                        {texts.VOTRE_SOCIETE}
                    </Typography>
                    <CustomTextField
                        value={form.corporateName}
                        onChange={handleChange('corporateName')}
                        label={texts.DENOMINATION}
                        inputStyle={classes.field}
                        showError={!tunnelErrors.corporateName}
                        error={errors.corporateName || tunnelErrors.corporateName}
                    />
                    <CustomSelect
                        value={form.corporateForm}
                        onChange={handleChange('corporateForm')}
                        label={texts.FORME_SOCIALE}
                        inputStyle={classes.field}
                        error={errors.corporateForm || tunnelErrors.corporateForm}
                    >
                        {Object.values(corporateForms).map(corporateForm => (
                            <MenuItem key={corporateForm} value={corporateForm}>
                                {corporateForm}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                    {form.corporateForm === corporateForms.other && (
                        <CustomTextField
                            showError
                            value={form.otherCorporateForm}
                            onChange={handleChange('otherCorporateForm')}
                            inputStyle={classes.field}
                            error={errors.otherCorporateForm}
                        />
                    )}
                    <FormControl component="fieldset" className={classes.field}>
                        <CustomFormLabel>{texts.ORGANISATION}</CustomFormLabel>
                        <RadioGroup
                            value={form.organization}
                            onChange={handleChange('organization')}
                        >
                            <RadioLabel value="true" label={texts.RESEAU} />
                            <RadioLabel value="false" label={texts.INDEPENDANT} />
                            {!!errors.organization && (
                                <ErrorMessage gutterBottom message={errors.organization} />
                            )}
                        </RadioGroup>
                    </FormControl>
                    <Box display="flex" alignItems="flex-end">
                        <CustomTextField
                            inputStyle={classes.field}
                            label={texts.CAPITAL_SOCIAL + PARANTHESES_EURO_SYMBOL}
                            value={form.shareCapital}
                            onChange={handleChange('shareCapital')}
                            error={errors.shareCapital || tunnelErrors.shareCapital}
                            showError={!tunnelErrors.shareCapital}
                            formatNumber
                        />
                    </Box>
                    <FullAddressForm
                        label={texts.SIEGE_SOCIAL}
                        address={form.currentAddress}
                        onAddressChange={handleAddressChange}
                        errors={errors}
                    />
                    <Typography
                        variant="h6"
                        gutterBottom
                        className={clsx(classes.subtitle, classes.separator)}
                    >
                        {texts.YOU}
                    </Typography>
                    <CustomSelect
                        value={form.title}
                        onChange={handleChange('title')}
                        label={texts.CIVILITE}
                        inputStyle={classes.field}
                    >
                        {titles.map(renderOption)}
                    </CustomSelect>
                    <CustomTextField
                        value={form.firstName}
                        onChange={handleChange('firstName')}
                        label={texts.PRENOM}
                        inputStyle={classes.field}
                        showError={!tunnelErrors.firstName}
                        error={errors.firstName || tunnelErrors.firstName}
                    />
                    <CustomTextField
                        value={form.lastName}
                        onChange={handleChange('lastName')}
                        label={texts.NOM}
                        inputStyle={classes.field}
                        showError={!tunnelErrors.lastName}
                        error={errors.lastName || tunnelErrors.lastName}
                    />
                    <CustomSelect
                        inputStyle={classes.field}
                        label={texts.POSITION}
                        value={form.function}
                        onChange={handleChange('function')}
                    >
                        {positions.map(renderOption)}
                    </CustomSelect>
                    <CustomPhoneField
                        sx={theme => ({ width: `${theme.constants.tunnelInputWidth}px` })}
                        label={texts.YOUR_PHONE}
                        value={form.phoneNumber}
                        onChange={handlePhoneChange}
                        error={errors.phoneNumber || tunnelErrors.phoneNumber}
                        showError={!tunnelErrors.phoneNumber}
                    />
                    <CustomTextField
                        type="email"
                        inputStyle={classes.field}
                        label={texts.YOUR_EMAIL}
                        value={form.email}
                        disabled={!!internalUser}
                        onChange={handleChange('email')}
                        error={emailExists ? texts.CETTE_ADRESSE_EXISTE_DEJA : errors.email}
                    />
                    <Typography
                        align="left"
                        variant="body1"
                        gutterBottom
                        className={clsx(classes.separator, classes.labelColor)}
                    >
                        {texts.LEGAL_REPRESENTATIVE}
                    </Typography>
                    <FormControl component="fieldset" className={classes.field}>
                        <RadioGroup
                            value={form.legalRepresentant}
                            onChange={handleChange('legalRepresentant')}
                        >
                            <RadioLabel value="yes" label={texts.OUI} />
                            <RadioLabel value="no" label={texts.NON} />
                            {!!errors.legalRepresentant && (
                                <ErrorMessage gutterBottom message={errors.legalRepresentant} />
                            )}
                        </RadioGroup>
                    </FormControl>
                    {form.legalRepresentant === 'no' && (
                        <>
                            <CustomTextField
                                value={form.representativeName}
                                onChange={handleChange('representativeName')}
                                label={texts.REPRESENTATIVE_FULL_NAME}
                                inputStyle={classes.field}
                                showError={!tunnelErrors.representativeName}
                                error={errors.representativeName || tunnelErrors.representativeName}
                            />
                            <CustomSelect
                                inputStyle={classes.field}
                                label={texts.LEGAL_REPRESENTATIVE_POSITION}
                                value={form.representativeRole}
                                onChange={handleChange('representativeRole')}
                            >
                                {positions.map(renderOption)}
                            </CustomSelect>
                        </>
                    )}
                    {!internalUser && (
                        <PasswordForm
                            form={form}
                            classes={classes}
                            user={internalUser}
                            title={texts.MERCI_POUR_MOT_DE_PASSE}
                            handleChange={handleChange}
                            errors={errors}
                        />
                    )}
                    <ConditionsForm
                        conditions={form.conditions}
                        onChange={handleConditionsChange}
                    />
                    {errors['conditions,termsAgreement'] && (
                        <ErrorMessage
                            gutterBottom
                            message={texts.VOUS_DEVEZ_ACCEPTER_LES_CONDITIONS}
                        />
                    )}
                    {errors['conditions,acceptedEmails'] && (
                        <ErrorMessage
                            gutterBottom
                            message={texts.VOUS_DEVEZ_ACCEPTER_D_ETRE_CONTACTEE}
                        />
                    )}
                    {isSuccess && isResubmit && (
                        <Typography align="left" variant="body2">
                            {texts.YOUR_INFO_SAVED}
                        </Typography>
                    )}
                </TunnelBox>
            )}
            {isSuccess && !userStep && (
                <Typography align="center" variant="body2">
                    {texts.SHOULD_CLOSE_PAGE}
                </Typography>
            )}
            {(!isSuccess || userStep) && (
                <SubmitButton
                    disabled={!allowSubmit || isLoading}
                    text={
                        (isResubmit || isAccountNotConfirmed) && changesMade
                            ? texts.SAVE_AND_CONTINUE
                            : texts.SUIVANT
                    }
                    loading={isLoading}
                    onClick={changesMade ? handleSubmit() : handleNextStep}
                />
            )}
        </React.Fragment>
    );
};

DistributorForm.propTypes = {
    userStep: PropTypes.object,
    roleName: PropTypes.string,
    tunnelStep: PropTypes.object,
    handleNextStep: PropTypes.func,
};

export default DistributorForm;
